/**
 * Helper function to round a decimal number to a given precision of a provided list of precisions.
 *
 * The precision list is an array of numbers, each number representing a precision:
 *
 * - 0: 1
 * - 1: 0.5
 * - 2: 0.75
 * - 3: 0.667 (This is the round up of 2/3)
 * - 4: 0.25
 *
 * @param value
 * @returns {number|*}
 */
export function roundDecimal(value) {
    // If the value is an integer, return as is
    if (Number.isInteger(value)) {
        return value;
    }

    const decimals = [1, 0.5, 0.75, 0.667, 0.25];
    const decimalPart = value % 1; // Get the decimal part
    const integerPart = Math.floor(value); // Get the integer part

    let nearestDecimal = decimals[0];
    let smallestDifference = Math.abs(decimalPart - decimals[0]);

    for (let i = 1; i < decimals.length; i+= 1) {
        const difference = Math.abs(decimalPart - decimals[i]);
        if (difference < smallestDifference) {
            smallestDifference = difference;
            nearestDecimal = decimals[i];
        }
    }

    return integerPart + nearestDecimal;
}

export function fractionToDecimal(fractionString) {
    // Map of unicode fractions to decimals
    const fractionMap = {
        '¼': 0.25,
        '½': 0.5,
        '¾': 0.75,
        '⅓': 1 / 3,
        '⅔': 2 / 3,
        '⅕': 0.2,
        '⅖': 0.4,
        '⅗': 0.6,
        '⅘': 0.8,
        '⅙': 1 / 6,
        '⅚': 5 / 6,
        '⅛': 0.125,
        '⅜': 0.375,
        '⅝': 0.625,
        '⅞': 0.875,
    };

    // Function to convert fraction to decimal
    function fracToDec(frac) {
        if (frac in fractionMap) {
            return fractionMap[frac];
        }
            const parts = frac.split('/');
            if (parts.length < 2) {
                // This is an integer, not a fraction
                return parseInt(parts[0], 10);
            }
                return parseFloat(parts[0]) / parseFloat(parts[1]);


    }

    const parts = fractionString.trim().split(' ');

    if (parts.length === 1) {
        // This is a single fraction or integer
        return fracToDec(parts[0]);
    }
        // This is a mixed number
        return parseInt(parts[0], 10) + fracToDec(parts[1]);

}

export function decimalToFraction(value) {
    // Map of decimals to unicode fractions
    const decimalMap = {
        1: '',
        0.5: '½',
        0.75: '¾',
        0.67: '⅔',
        0.33: '⅓',
        0.25: '¼',
        0.125: '⅛',
    };

    // Function to convert decimal to fraction
    function decToFrac(dec) {
        const decimal = parseFloat(dec.toFixed(2)); // Round to 2 decimal places

        // Iterate over keys in the map
        const foundKey = Object.keys(decimalMap).find((key) => Math.abs(key - decimal) < 0.01);
        if (foundKey) {
            return decimalMap[foundKey];
        }

        return '';
    }

    const integerPart = Math.floor(value); // Get the integer part
    const decimalPart = parseFloat((value % 1).toFixed(2)); // Get the decimal part

    if (integerPart === 0) {
        // This is a pure fraction
        return decToFrac(decimalPart);
    }
        // This is a whole or mixed number
        const fractionPart = decToFrac(decimalPart);
        return fractionPart ? `${integerPart} ${fractionPart}` : integerPart.toString();

}
